import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';

// Typen für das Eroberungs-System
interface Territory {
  id: string;
  name: string;
  icon: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'extreme' | 'legendary';
  requiredLevel: number;
  gardenBoostType: string; // Welcher Gartenbereich profitiert
  maxLevelIncrease: number; // Um wie viel das Max-Level erhöht wird
  conquestTime: number; // Zeit in Minuten
  enemyPower: number;
}

const CompanionConquest: React.FC = () => {
  const { 
    companions, 
    formatNumber, 
    playerLevel,
    startConquest,
    getConquestTimeRemaining,
    isCompanionOnConquest,
    gardenAreas,
    updateConquests
  } = useGameStore();

  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [selectedTerritory, setSelectedTerritory] = useState<Territory | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Alle verfügbaren Territorien für alle Gartenbereiche
  const territories: Territory[] = [
    // Basis Bereiche
    {
      id: 'grasslands',
      name: 'Grüne Wiesen',
      icon: '🌱',
      description: 'Endlose grüne Wiesen mit fruchtbarem Boden',
      difficulty: 'easy',
      requiredLevel: 1,
      gardenBoostType: 'Rasen',
      maxLevelIncrease: 25,
      conquestTime: 1,
      enemyPower: 50,
    },
    {
      id: 'meadow',
      name: 'Blumenwiese',
      icon: '🌸',
      description: 'Ein friedliches Gebiet voller wilder Blumen',
      difficulty: 'easy',
      requiredLevel: 3,
      gardenBoostType: 'Blumenbeet',
      maxLevelIncrease: 30,
      conquestTime: 20,
      enemyPower: 75,
    },
    {
      id: 'crystalLake',
      name: 'Kristallsee',
      icon: '🏞️',
      description: 'Ein klarer See mit magischen Eigenschaften',
      difficulty: 'easy',
      requiredLevel: 5,
      gardenBoostType: 'Teich',
      maxLevelIncrease: 35,
      conquestTime: 25,
      enemyPower: 100,
    },
    {
      id: 'stoneTerrace',
      name: 'Steinterrasse',
      icon: '🏛️',
      description: 'Alte Steinterrassen mit geheimen Kammern',
      difficulty: 'medium',
      requiredLevel: 7,
      gardenBoostType: 'Terrasse',
      maxLevelIncrease: 40,
      conquestTime: 30,
      enemyPower: 150,
    },
    {
      id: 'glasshouse',
      name: 'Kristallgewächshaus',
      icon: '🏠',
      description: 'Ein magisches Gewächshaus aus purem Kristall',
      difficulty: 'medium',
      requiredLevel: 10,
      gardenBoostType: 'Gewächshaus',
      maxLevelIncrease: 45,
      conquestTime: 35,
      enemyPower: 200,
    },
    {
      id: 'ancientFountain',
      name: 'Uralter Brunnen',
      icon: '⛲',
      description: 'Ein alter Brunnen mit ewigem Wasser',
      difficulty: 'medium',
      requiredLevel: 12,
      gardenBoostType: 'Springbrunnen',
      maxLevelIncrease: 50,
      conquestTime: 40,
      enemyPower: 250,
    },

    // Erweiterte Bereiche
    {
      id: 'darkForest',
      name: 'Dunkler Wald',
      icon: '🌲',
      description: 'Ein mysteriöser Wald mit wertvollen Ressourcen',
      difficulty: 'medium',
      requiredLevel: 15,
      gardenBoostType: 'Obstgarten',
      maxLevelIncrease: 55,
      conquestTime: 45,
      enemyPower: 300,
    },
    {
      id: 'farmlands',
      name: 'Fruchtbare Felder',
      icon: '🥕',
      description: 'Weite Felder mit den besten Gemüsesorten',
      difficulty: 'medium',
      requiredLevel: 17,
      gardenBoostType: 'Gemüsebeet',
      maxLevelIncrease: 60,
      conquestTime: 50,
      enemyPower: 350,
    },
    {
      id: 'roseValley',
      name: 'Rosental',
      icon: '🌹',
      description: 'Ein Tal voller duftender Rosen',
      difficulty: 'hard',
      requiredLevel: 20,
      gardenBoostType: 'Rosengarten',
      maxLevelIncrease: 65,
      conquestTime: 55,
      enemyPower: 400,
    },
    {
      id: 'zenTemple',
      name: 'Zen-Tempel',
      icon: '🏯',
      description: 'Ein friedlicher Tempel der Meditation',
      difficulty: 'hard',
      requiredLevel: 22,
      gardenBoostType: 'Japanischer Garten',
      maxLevelIncrease: 70,
      conquestTime: 60,
      enemyPower: 450,
    },
    {
      id: 'mysticSwamp',
      name: 'Mystischer Sumpf',
      icon: '🐸',
      description: 'Ein nebelverhangener Sumpf mit magischen Kräutern',
      difficulty: 'hard',
      requiredLevel: 25,
      gardenBoostType: 'Wildblumenwiese',
      maxLevelIncrease: 75,
      conquestTime: 65,
      enemyPower: 500,
    },
    {
      id: 'rockyMountains',
      name: 'Steinige Berge',
      icon: '⛰️',
      description: 'Gefährliche Berge mit seltenen Skulpturen',
      difficulty: 'hard',
      requiredLevel: 27,
      gardenBoostType: 'Skulpturengarten',
      maxLevelIncrease: 80,
      conquestTime: 70,
      enemyPower: 550,
    },

    // Premium Bereiche
    {
      id: 'enchantedGrove',
      name: 'Verzauberter Hain',
      icon: '🧚',
      description: 'Ein magischer Hain voller Feen und Magie',
      difficulty: 'extreme',
      requiredLevel: 30,
      gardenBoostType: 'Verzauberter Hain',
      maxLevelIncrease: 85,
      conquestTime: 75,
      enemyPower: 600,
    },
    {
      id: 'crystalCave',
      name: 'Kristallhöhle',
      icon: '💎',
      description: 'Eine Höhle voller leuchtender Kristalle',
      difficulty: 'extreme',
      requiredLevel: 32,
      gardenBoostType: 'Kristallhöhle',
      maxLevelIncrease: 90,
      conquestTime: 80,
      enemyPower: 650,
    },
    {
      id: 'moonlightGarden',
      name: 'Mondschein-Garten',
      icon: '🌙',
      description: 'Ein Garten der nur im Mondlicht blüht',
      difficulty: 'extreme',
      requiredLevel: 35,
      gardenBoostType: 'Mondschein-Garten',
      maxLevelIncrease: 95,
      conquestTime: 85,
      enemyPower: 700,
    },
    {
      id: 'dragonLair',
      name: 'Drachenhort',
      icon: '🐉',
      description: 'Der legendäre Hort eines uralten Drachen',
      difficulty: 'legendary',
      requiredLevel: 40,
      gardenBoostType: 'Drachenhort',
      maxLevelIncrease: 100,
      conquestTime: 90,
      enemyPower: 800,
    },

    // Klick-fokussierte Bereiche
    {
      id: 'goldMine',
      name: 'Goldmine',
      icon: '⛏️',
      description: 'Eine tiefe Mine voller glänzendem Gold',
      difficulty: 'extreme',
      requiredLevel: 45,
      gardenBoostType: 'Goldmine',
      maxLevelIncrease: 120,
      conquestTime: 100,
      enemyPower: 900,
    },
    {
      id: 'sandDesert',
      name: 'Sandwüste',
      icon: '🏜️',
      description: 'Eine endlose Wüste mit verborgenen Schätzen',
      difficulty: 'extreme',
      requiredLevel: 50,
      gardenBoostType: 'Schatzkammer',
      maxLevelIncrease: 140,
      conquestTime: 110,
      enemyPower: 1000,
    },
    {
      id: 'alchemyTower',
      name: 'Alchemie-Turm',
      icon: '⚗️',
      description: 'Ein hoher Turm voller alchemistischer Geheimnisse',
      difficulty: 'legendary',
      requiredLevel: 55,
      gardenBoostType: 'Alchemie-Labor',
      maxLevelIncrease: 160,
      conquestTime: 120,
      enemyPower: 1200,
    },
    {
      id: 'fireVolcano',
      name: 'Feuervulkan',
      icon: '🌋',
      description: 'Ein aktiver Vulkan mit mächtigen Feuerkristallen',
      difficulty: 'legendary',
      requiredLevel: 60,
      gardenBoostType: 'Magische Schmiede',
      maxLevelIncrease: 180,
      conquestTime: 150,
      enemyPower: 1500,
    },
  ];

  // Begleiter auf Eroberung
  const companionsOnConquest = companions.discovered
    .filter(companion => isCompanionOnConquest(companion.id))
    .map(companion => {
      const conquest = companions.conquest?.activeConquests[companion.id];
      if (!conquest) return null;
      
      // Berechne verbleibende Zeit basierend auf aktueller Zeit
      const startTime = new Date(conquest.startTime).getTime();
      const elapsed = currentTime - startTime;
      const totalDuration = conquest.duration * 60 * 1000; // Minuten zu Millisekunden
      const timeRemaining = Math.max(0, totalDuration - elapsed);
      
      return {
        companion,
        timeRemaining,
        area: conquest.area || 'Unbekannt',
        progress: Math.min(100, (elapsed / totalDuration) * 100),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Eroberte Territorien mit Anzahl
  const territoryConquestCounts = companions.conquest?.territoryConquestCounts || {};
  const totalConquests = Object.values(territoryConquestCounts).reduce((sum, count) => sum + count, 0);

  // Verfügbare Begleiter (nicht auf Eroberung, nicht im Training)
  const availableCompanions = companions.discovered.filter(companion => 
    !isCompanionOnConquest(companion.id) &&
    !companion.isTraining
  );

  // Timer für Live-Updates - häufiger für flüssigere Animationen
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
      updateConquests();
    }, 100); // Alle 100ms für flüssigere Animationen

    return () => clearInterval(interval);
  }, [updateConquests]);

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty: Territory['difficulty']) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400 border-green-400/30';
      case 'medium': return 'text-yellow-400 border-yellow-400/30';
      case 'hard': return 'text-orange-400 border-orange-400/30';
      case 'extreme': return 'text-red-400 border-red-400/30';
      case 'legendary': return 'text-purple-400 border-purple-400/30';
      default: return 'text-gray-400 border-gray-400/30';
    }
  };

  const handleStartConquest = () => {
    if (!selectedCompanion || !selectedTerritory) return;

    const success = startConquest(
      selectedCompanion.id,
      selectedTerritory.name,
      selectedTerritory.conquestTime
    );

    if (success) {
      setSelectedCompanion(null);
      setSelectedTerritory(null);
    }
  };

  const getGardenAreaName = (territoryName: string): string => {
    const territory = territories.find(t => t.name === territoryName);
    return territory?.gardenBoostType || territoryName;
  };

  const getGardenAreaKey = (territoryName: string): keyof typeof gardenAreas | null => {
    const mapping: { [key: string]: keyof typeof gardenAreas } = {
      'Grüne Wiesen': 'lawn',
      'Blumenwiese': 'flowerBed',
      'Kristallsee': 'pond',
      'Steinterrasse': 'terrace',
      'Kristallgewächshaus': 'greenhouse',
      'Uralter Brunnen': 'fountain',
      'Dunkler Wald': 'orchard',
      'Fruchtbare Felder': 'veggiePatch',
      'Rosental': 'roseGarden',
      'Zen-Tempel': 'japaneseGarden',
      'Mystischer Sumpf': 'wildflowerMeadow',
      'Steinige Berge': 'sculptureGarden',
      'Verzauberter Hain': 'enchantedGrove',
      'Kristallhöhle': 'crystalCave',
      'Mondschein-Garten': 'moonlightGarden',
      'Drachenhort': 'dragonLair',
      'Goldmine': 'goldMine',
      'Sandwüste': 'treasureVault',
      'Alchemie-Turm': 'alchemyLab',
      'Feuervulkan': 'magicForge',
    };
    return mapping[territoryName] || null;
  };

  const getConquestCount = (territoryName: string): number => {
    return territoryConquestCounts[territoryName] || 0;
  };

  const getRarityName = (type: string) => {
    switch (type) {
      case 'legendary': return 'Legendär';
      case 'epic': return 'Episch';
      case 'rare': return 'Selten';
      case 'common': return 'Gewöhnlich';
      default: return 'Unbekannt';
    }
  };

  return (
    <div className="p-4 space-y-6">


      {/* Statistiken */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="bg-blue-600/60 rounded-lg p-2 sm:p-3 border border-blue-400/50 text-center">
          <div className="text-blue-200 text-xs font-bold mb-1">Gebiete</div>
          <div className="text-white text-base sm:text-lg font-bold">
            {territories.length}
          </div>
        </div>
        
        <div className="bg-green-600/60 rounded-lg p-2 sm:p-3 border border-green-400/50 text-center">
          <div className="text-green-200 text-xs font-bold mb-1">Eroberungen</div>
          <div className="text-white text-base sm:text-lg font-bold">
            {totalConquests}
          </div>
        </div>
        
        <div className="bg-yellow-600/60 rounded-lg p-2 sm:p-3 border border-yellow-400/50 text-center">
          <div className="text-yellow-200 text-xs font-bold mb-1">Expeditionen</div>
          <div className="text-white text-base sm:text-lg font-bold">
            {companionsOnConquest.length}
          </div>
        </div>
        
        <div className="bg-purple-600/60 rounded-lg p-2 sm:p-3 border border-purple-400/50 text-center">
          <div className="text-purple-200 text-xs font-bold mb-1">Begleiter</div>
          <div className="text-white text-base sm:text-lg font-bold">
            {availableCompanions.length}
          </div>
        </div>
      </div>

      {/* Aktive Eroberungen */}
      {companionsOnConquest.length > 0 && (
        <div className="bg-blue-600/60 rounded-xl p-4 border border-blue-400/50">
          <h4 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>⚔️</span>
            Aktive Eroberungen ({companionsOnConquest.length})
          </h4>
          
          <div className="space-y-3">
            {companionsOnConquest.map(({ companion, timeRemaining, area, progress }) => (
              <div key={companion.id} className="bg-blue-500/60 rounded-lg p-3 border border-blue-400/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">
                      {companion.icon.startsWith('data:') ? (
                        <img 
                          src={companion.icon} 
                          alt={companion.name}
                          className="w-8 h-8 object-contain"
                          style={{ display: 'inline-block' }}
                        />
                      ) : (
                        companion.icon
                      )}
                    </div>
                    <div>
                      <div className="text-white font-bold">{companion.name}</div>
                      <div className="text-blue-300 text-sm">Erobert: {area}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">
                      {timeRemaining > 0 ? formatTime(timeRemaining) : 'Abgeschlossen!'}
                    </div>
                    <div className="text-blue-300 text-sm">verbleibend</div>
                  </div>
                </div>
                
                {/* Fortschrittsbalken */}
                <div className="w-full bg-blue-900/50 rounded-full h-2">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ 
                      width: `${progress}%`
                    }}
                    transition={{ duration: 0.1, ease: "linear" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Eroberte Territorien */}
      {totalConquests > 0 && (
        <div className="bg-green-600/60 rounded-xl p-4 border border-green-400/50">
          <h4 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>🏆</span>
            Eroberte Territorien (Top 10)
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(territoryConquestCounts)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 10)
              .map(([territoryName, count]) => {
                const territory = territories.find(t => t.name === territoryName);
                const gardenAreaKey = getGardenAreaKey(territoryName);
                const gardenArea = gardenAreaKey ? gardenAreas[gardenAreaKey] : null;
                
                return (
                  <div key={territoryName} className="bg-green-500/60 rounded-lg p-3 border border-green-400/50">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-2xl">{territory?.icon || '🏴'}</div>
                      <div className="flex-1">
                        <div className="text-white font-bold">{territoryName}</div>
                        <div className="text-green-300 text-sm">
                          {getGardenAreaName(territoryName)} +{(territory?.maxLevelIncrease || 0) * count} Max-Level
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-green-400 font-bold text-lg">{count}x</div>
                        <div className="text-green-300 text-xs">erobert</div>
                      </div>
                    </div>
                    
                    {gardenArea && (
                      <div className="text-green-200 text-xs">
                        Aktuelles Max-Level: {formatNumber(gardenArea.maxLevel)}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Territorien-Übersicht - Nach Schwierigkeit gruppiert */}
      <div className="space-y-4">
        {['easy', 'medium', 'hard', 'extreme', 'legendary'].map(difficulty => {
          const difficultyTerritories = territories.filter(t => t.difficulty === difficulty);
          if (difficultyTerritories.length === 0) return null;

          return (
            <div key={difficulty}>
              <h3 className={`text-lg font-bold mb-3 ${getDifficultyColor(difficulty as Territory['difficulty']).split(' ')[0]}`}>
                {difficulty.toUpperCase()} ({difficultyTerritories.length})
              </h3>
              
              <div className="grid grid-cols-1 gap-3">
                {difficultyTerritories.map(territory => {
                  const conquestCount = getConquestCount(territory.name);
                  const canConquer = playerLevel >= territory.requiredLevel;
                  
                  return (
                    <motion.div
                      key={territory.id}
                      className={`p-4 rounded-xl border transition-all duration-300 ${
                        canConquer
                          ? 'bg-gray-800/80 border-gray-600/60 hover:border-blue-400/70 cursor-pointer'
                          : 'bg-gray-900/80 border-gray-700/60 opacity-50'
                      } ${selectedTerritory?.id === territory.id ? 'ring-2 ring-blue-400' : ''}`}
                      onClick={() => canConquer ? setSelectedTerritory(territory) : null}
                      whileHover={canConquer ? { scale: 1.02 } : {}}
                      whileTap={canConquer ? { scale: 0.98 } : {}}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon und Status */}
                        <div className="flex flex-col items-center">
                          <div className="text-4xl mb-2">{territory.icon}</div>
                          {conquestCount > 0 && (
                            <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                              {conquestCount}x
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                            <h3 className="text-white font-bold text-lg">{territory.name}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-bold border w-fit ${getDifficultyColor(territory.difficulty)}`}>
                              {territory.difficulty.toUpperCase()}
                            </span>
                          </div>
                          
                          <p className="text-gray-300 text-sm mb-3">{territory.description}</p>
                          
                          {/* Stats Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-xs">
                            <div className="bg-blue-600/60 rounded p-1.5 sm:p-2">
                              <div className="text-blue-200 font-bold text-xs">Level</div>
                              <div className="text-white font-bold">{territory.requiredLevel}</div>
                            </div>
                            
                            <div className="bg-purple-600/60 rounded p-1.5 sm:p-2">
                              <div className="text-purple-200 font-bold text-xs">Zeit</div>
                              <div className="text-white font-bold">{territory.conquestTime}min</div>
                            </div>
                            
                            <div className="bg-green-600/60 rounded p-1.5 sm:p-2">
                              <div className="text-green-200 font-bold text-xs">Bereich</div>
                              <div className="text-white font-bold">{territory.gardenBoostType}</div>
                            </div>
                            
                            <div className="bg-yellow-600/60 rounded p-1.5 sm:p-2">
                              <div className="text-yellow-200 font-bold text-xs">Bonus</div>
                              <div className="text-white font-bold">+{territory.maxLevelIncrease}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Begleiter-Auswahl Modal */}
      <AnimatePresence key="companion-selection-modal">
        {selectedTerritory && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTerritory(null)}
          >
            <motion.div
              className="bg-gray-800 rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">
                {selectedTerritory.icon} {selectedTerritory.name} erobern
              </h3>
              
              <div className="mb-4 p-3 bg-blue-600/60 rounded-lg border border-blue-400/50">
                <div className="text-blue-200 text-sm font-bold mb-1">Belohnung</div>
                <div className="text-white text-sm">
                  {selectedTerritory.gardenBoostType} Max-Level +{selectedTerritory.maxLevelIncrease}
                </div>
                {getConquestCount(selectedTerritory.name) > 0 && (
                  <div className="text-green-200 text-xs mt-1">
                    Bereits {getConquestCount(selectedTerritory.name)}x erobert
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <h4 className="text-white font-bold mb-2">Begleiter auswählen:</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {availableCompanions.length === 0 ? (
                    <div className="text-gray-400 text-center py-4">
                      Keine verfügbaren Begleiter
                    </div>
                  ) : (
                    availableCompanions.map(companion => {
                      const isActive = companions.active.some(active => active && active.id === companion.id);
                      return (
                        <div
                          key={companion.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedCompanion?.id === companion.id
                              ? 'bg-blue-600/70 border-blue-400'
                              : 'bg-gray-700/80 border-gray-600 hover:border-blue-400/70'
                          } ${isActive ? 'ring-2 ring-yellow-400/70' : ''}`}
                          onClick={() => setSelectedCompanion(companion)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="text-2xl">
                              {companion.icon.startsWith('data:') ? (
                                <img 
                                  src={companion.icon} 
                                  alt={companion.name}
                                  className="w-8 h-8 object-contain"
                                  style={{ display: 'inline-block' }}
                                />
                              ) : (
                                companion.icon
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="text-white font-bold">{companion.name}</div>
                              <div className="text-gray-300 text-sm">{getRarityName(companion.type)}</div>
                              {isActive && (
                                <div className="text-yellow-400 text-xs mt-1">
                                  ⚠️ Aktiver Begleiter - wird während Eroberung deaktiviert
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                  onClick={() => setSelectedTerritory(null)}
                >
                  Abbrechen
                </button>
                <button
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                    selectedCompanion
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                  onClick={handleStartConquest}
                  disabled={!selectedCompanion}
                >
                  Eroberung starten
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanionConquest; 